import {
  useRestoreTenant,
  useTenantHistory,
} from '@/api/hooks/useTenantHistory'
import { HistoryButton, HistoryDrawer } from '@/components/History'
import { useSecurityContext } from '@/contexts/SecurityContext'
import type { Tenant } from '@/stores/tenantStore'
import { formatCompactDateTime } from '@/utils/dateFormatters'
import { PlusOutlined } from '@ant-design/icons'
import {
  useCreateTenant,
  useTenantsList,
  useUpdateTenant,
} from '@api/hooks/useCrossroads'
import { GraviGrid, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { Button, Checkbox, Form, Input, Modal, message } from 'antd'
import { useState } from 'react'

export const TenantsGrid = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null)
  const [historyTenant, setHistoryTenant] = useState<Tenant | null>(null)
  const [form] = Form.useForm()

  const { data: tenants, isLoading, refetch } = useTenantsList()
  const createMutation = useCreateTenant()
  const updateMutation = useUpdateTenant()
  // isFetching, not isLoading: a reopen refetches over cached data, and the
  // drawer should show that rather than the previous result.
  const { data: history, isFetching: isHistoryFetching } = useTenantHistory(
    historyTenant?.id,
  )
  const restoreMutation = useRestoreTenant()
  // /meta/tenant/restore is gated on gravitate_admin_access, so only offer it
  // to users the backend would accept.
  const { isGravitateAdmin } = useSecurityContext()
  const [restoringAuditId, setRestoringAuditId] = useState<string | null>(null)

  const columnDefs: ColDef[] = [
    {
      field: 'goid',
      headerName: 'GOID',
      width: 120,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.5,
    },
    {
      field: 'created',
      headerName: 'Created',
      width: 180,
      valueFormatter: (params) => formatCompactDateTime(params.value),
    },
    {
      field: 'instances',
      headerName: 'Instances',
      width: 100,
      valueGetter: (params) => params.data?.instances?.length ?? 0,
    },
    {
      field: 'partners',
      headerName: 'Partners',
      width: 100,
      valueGetter: (params) => params.data?.partners?.length ?? 0,
    },
    {
      field: 'scac',
      headerName: 'SCAC',
      width: 100,
      editable: true,
    },
    {
      field: 'pull_site_mrds',
      headerName: 'Pull MRDs',
      width: 100,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: [true, false],
      },
      valueFormatter: (params) => (params.value ? 'True' : 'False'),
    },
    {
      headerName: '',
      colId: 'history',
      pinned: 'right',
      width: 56,
      initialFlex: 0,
      minWidth: 56,
      maxWidth: 56,
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (params: ICellRendererParams<Tenant>) => {
        const tenant = params.data
        if (!tenant) return null
        return <HistoryButton onClick={() => setHistoryTenant(tenant)} />
      },
    },
  ]

  const handleCellValueChanged = (tenant: Tenant) => {
    updateMutation.mutate(
      {
        tenant_id: tenant.id,
        body: {
          pull_site_mrds: tenant.pull_site_mrds,
          scac: tenant.scac || null,
        },
      },
      {
        onSuccess: () => {
          message.success('Tenant updated successfully')
          refetch()
        },
        onError: () => {
          message.error('Failed to update tenant')
        },
      },
    )
  }

  // The drawer closes on success: historyTenant is a snapshot of the row taken
  // at click time, so leaving it open would diff against a pre-restore state.
  const handleRestore = (auditId: string) => {
    if (!historyTenant) return
    setRestoringAuditId(auditId)
    restoreMutation.mutate(
      {
        params: {
          query: { audit_id: auditId, tenant_id: historyTenant.id },
          header: { 'X-Tenant-Name': 'Gravitate' },
        },
      },
      {
        onSuccess: () => {
          setHistoryTenant(null)
          refetch()
        },
        onSettled: () => setRestoringAuditId(null),
      },
    )
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      if (editingTenant) {
        updateMutation.mutate(
          {
            tenant_id: editingTenant.id,
            body: {
              name: values.name,
              pull_site_mrds: values.pull_site_mrds ?? false,
              scac: values.scac || null,
            },
          },
          {
            onSuccess: () => {
              message.success('Tenant updated successfully')
              setIsModalOpen(false)
              refetch()
            },
          },
        )
      } else {
        createMutation.mutate(
          {
            name: values.name,
            pull_site_mrds: values.pull_site_mrds ?? false,
            scac: values.scac || null,
          },
          {
            onSuccess: () => {
              message.success('Tenant created successfully')
              setIsModalOpen(false)
              refetch()
            },
          },
        )
      }
    })
  }

  return (
    <Vertical bg='surface' style={{ height: '100%', width: '100%' }}>
      <GraviGrid
        rowData={tenants}
        columnDefs={columnDefs}
        loading={isLoading}
        updateEP={handleCellValueChanged as unknown as (params: never) => void}
        controlBarProps={{
          title: 'Tenants',
          actionButtons: (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTenant(null)
                form.resetFields()
                setIsModalOpen(true)
              }}
            >
              Add Tenant
            </Button>
          ),
        }}
        columnDefaultOverrides={{
          editable: false,
          sortable: true,
          filter: true,
          resizable: true,
        }}
        agPropOverrides={{
          getRowId: (params) => params.data.id,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
        }}
      />
      <Modal
        title={editingTenant ? 'Edit Tenant' : 'Add Tenant'}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => setIsModalOpen(false)}
      >
        <Form form={form} layout='vertical'>
          <Form.Item name='name' label='Name' rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name='scac' label='SCAC'>
            <Input />
          </Form.Item>
          <Form.Item
            name='pull_site_mrds'
            valuePropName='checked'
            initialValue={false}
          >
            <Checkbox>Pull Site MRDs</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
      <HistoryDrawer
        open={!!historyTenant}
        onClose={() => setHistoryTenant(null)}
        history={history}
        current={historyTenant ?? undefined}
        loading={isHistoryFetching}
        title={historyTenant ? `${historyTenant.name} History` : 'History'}
        emptyDescription='No changes recorded for this tenant'
        onRestore={isGravitateAdmin() ? handleRestore : undefined}
        restoringAuditId={restoringAuditId}
      />
    </Vertical>
  )
}
